import { useCallback, useRef, type KeyboardEvent } from 'react'

import { DisplayImage } from '~/components/DisplayImage'
import { Tooltip } from '~/components/component-library'
import { cn } from '~/utils/tailwind-utils'

export const PhotoEdit = ({
  photoUrl,
  photoAlt,
  isDisabled = false,
  processFile,
  shape,
  size = 'lg',
}: {
  photoUrl: string
  photoAlt: string
  isDisabled?: boolean
  processFile: (file: File) => Promise<void>
  shape?: 'rounded' | 'circular'
  size?: 'sm' | 'md' | 'lg'
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      processFile(file)
    },
    [processFile],
  )

  const triggerFileSelect = useCallback(() => {
    if (isDisabled) return

    fileInputRef.current?.click()
  }, [isDisabled])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return

      event.preventDefault()
      triggerFileSelect()
    },
    [triggerFileSelect],
  )

  if (isDisabled) {
    return (
      <div
        className={cn(
          'relative z-[10] inline-flex cursor-not-allowed items-center justify-center overflow-hidden border border-gray-200 opacity-60 dark:border-gray-700',
          {
            'rounded-full': shape === 'circular',
            'rounded-lg': shape === 'rounded',
            'h-12 w-12': size === 'sm',
            'h-16 w-16': size === 'md',
            'h-20 w-20': size === 'lg',
          },
        )}
      >
        <Tooltip content="Only workspace owners can change the workspace logo">
          <DisplayImage imageUrl={photoUrl} alt={photoAlt} />
        </Tooltip>
      </div>
    )
  }

  return (
    <div
      className={cn('group relative inline-block', {
        'rounded-full': shape === 'circular',
        'rounded-lg': shape === 'rounded',
        'h-12 w-12': size === 'sm',
        'h-16 w-16': size === 'md',
        'h-20 w-20': size === 'lg',
      })}
    >
      <div
        className={cn(
          'relative z-[10] inline-flex cursor-pointer items-center justify-center overflow-hidden border border-gray-200 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-gray-700',
          {
            'rounded-full': shape === 'circular',
            'rounded-lg': shape === 'rounded',
            'h-12 w-12': size === 'sm',
            'h-16 w-16': size === 'md',
            'h-20 w-20': size === 'lg',
          },
        )}
        role="button"
        onClick={triggerFileSelect}
        onKeyDown={handleKeyDown}
      >
        <DisplayImage imageUrl={photoUrl} alt={photoAlt} />
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg, image/gif, image/webp, image/avif"
        className="hidden"
        onChange={handleFileChange}
        disabled={isDisabled}
      />
    </div>
  )
}
